<template>
  <div>
    <BaseDropdown
      v-show="!isHidden"
      v-model="allocation.resourceId"
      :options="resourceListItems"
      :label="
        hideEmployeeLabel
          ? undefined
          : $t(`global.items.${resourceType}`, 1)
      "
      :loading="!isFirstAllocation && isFetchingRequirements"
      resources
      :required="!showAny"
      :disabled="disabled"
      :scrollContainer="scrollContainer"
      v-test="'part-resource'"
    />
  </div>
</template>

<script lang="ts">
import useVuelidate from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { storeToRefs } from 'pinia';
import type { PropType } from 'vue';
import { defineComponent, inject } from 'vue';
import { resourceIcon } from '@/helpers/formatting';
import {
  canAllocationBeAny,
  getAllocationResources,
  getValidAllocationResourceIds,
} from '@/modules/calendar/appointment/helpers';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import type { Requirement } from '@/stores/calendar-create-appointment/requirements';
import { useResourcesStore } from '@/stores/resources';
import type { AllocationAttributes, Resource } from '@/types';

export default defineComponent({
  props: {
    allocation: {
      type: Object as PropType<AllocationAttributes>,
      required: true,
    },
    partIndex: {
      type: Number as PropType<number>,
      required: true,
    },
    allocationIndex: Number as PropType<number>,
    disabled: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    allAllocations: {
      type: Array as PropType<AllocationAttributes[]>,
      required: true,
    },
    // The employee dropdown sits next to its service name in a package row, so the label is redundant there
    hideEmployeeLabel: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { isFetchingRequirements, isFetchingAvailabilities } = storeToRefs(
      useCreateAppointmentStore(),
    );
    const scrollContainer = inject<HTMLElement>('scrollContainer');

    return {
      isFetchingRequirements,
      isFetchingAvailabilities,
      v$: useVuelidate(),
      scrollContainer,
    };
  },
  validations() {
    return {
      allocation: {
        resourceId: {
          required: requiredIf(!this.showAny),
        },
      },
    };
  },
  watch: {
    validResourceIds: {
      handler: 'setDefaultResource',
      deep: true,
      immediate: true,
    },
    isFetchingData: 'setDefaultResource',
    'resources.length': {
      handler(value) {
        // When the location only has one resource of the selected type, select that resource by default
        if (value === 1) {
          this.allocation.resourceId = this.resources[0]?.id || null;
        }
      },
      immediate: true,
    },
  },
  computed: {
    isHidden() {
      const { hasSingleEmployee } = useResourcesStore();
      return (
        hasSingleEmployee &&
        this.allocationIndex === 0 &&
        this.resourceType === 'employee'
      );
    },
    isFetchingData() {
      return this.isFetchingRequirements || this.isFetchingAvailabilities;
    },
    requirement(): Requirement | null {
      const { requirementById } = useCreateAppointmentStore();
      return this.allocation.requirementId
        ? requirementById(this.allocation.requirementId) || null
        : null;
    },
    resourceType(): Resource['type'] {
      return this.requirement?.type.toLowerCase() || 'employee';
    },
    allocationOptionsInput() {
      const { resources, partAvailabilities } = useCreateAppointmentStore();
      return {
        allocation: this.allocation,
        allAllocations: this.allAllocations,
        requirement: this.requirement,
        resources,
        availableResourceIds:
          partAvailabilities[this.partIndex]?.availableResourceIds,
      };
    },
    resources(): Resource[] {
      // Only resources of the requirement's type, which are not used for other requirements
      return getAllocationResources(this.allocationOptionsInput);
    },
    resourceListItems(): any[] {
      const resources = this.resources.map((resource) => ({
        value: resource.id,
        label: resource.name,
        warnings: [
          {
            icon: 'alert',
            color: 'error',
            tooltip: this.$t('appointment.modal.part.requirement_warning', {
              name: resource.name,
            }),
            show: this.isNotInRequirements(resource.id),
          },
          {
            icon: 'calendar',
            color: 'warning',
            tooltip: this.$t('appointment.modal.part.availability_warning', {
              name: resource.name,
            }),
            show: this.isNotAvailable(resource.id),
          },
        ].filter((warning) => warning.show),
      }));

      if (this.showAny) {
        resources.unshift({
          label: this.$t('appointment.create.any_resource'),
          value: null,
          icon: resourceIcon(this.resourceType),
        });
      }

      return resources;
    },
    validResourceIds(): Resource['id'][] {
      // The id's of all resources that are in the requirements and also are available
      return getValidAllocationResourceIds(this.allocationOptionsInput);
    },
    isFirstAllocation(): boolean {
      return this.allocationIndex === 0;
    },
    showAny(): boolean {
      // Wether or not to show the "Any" option in the resource dropdown
      return canAllocationBeAny(this.allocationIndex, this.validResourceIds);
    },
  },
  methods: {
    isNotInRequirements(resourceId: Resource['id']): boolean {
      if (!this.requirement) {
        return false;
      }

      return !this.requirement.resourceIds.includes(resourceId);
    },
    isNotAvailable(resourceId: Resource['id']): boolean {
      const { partAvailabilities } = useCreateAppointmentStore();
      const availableResourceIds =
        partAvailabilities[this.partIndex]?.availableResourceIds;

      if (!availableResourceIds) {
        return false;
      }

      return !availableResourceIds.includes(resourceId);
    },
    setDefaultResource() {
      // Added a nextTick here because a test failed after adding a nextTick in the onAvailabilityDataChange method in the store.
      // I assumed this would not be needed, but it can't hurt.
      this.$nextTick(() => {
        // When there is only one valid resource, we need to set that as the selected resourceId
        // Don't do this if data is still being fetched (requirements or availabilities)
        // Or if its the first allocation, or it the allocation already has a resource selected
        if (
          this.isFetchingData ||
          this.isFirstAllocation ||
          this.allocation.resourceId
        ) {
          return;
        }

        if (this.validResourceIds.length === 1) {
          this.allocation.resourceId = this.validResourceIds[0];
        }
      });
    },
  },
});
</script>
